import { HttpClient } from '@angular/common/http'
import { Component, computed, inject, input, ChangeDetectionStrategy, Injectable } from '@angular/core'
import { rxResource } from '@angular/core/rxjs-interop'
import { AnimationItem } from 'lottie-web'
import { AnimationOptions, LottieComponent } from 'ngx-lottie'
import { Observable, catchError, of, shareReplay } from 'rxjs'

/**
 * Haelt je Animationsdatei EIN Observable — dieselbe Datei wird von vielen Bausteinen zugleich
 * angefragt (der Patienten-Avatar steht in jeder Zeile einer Liste). Ohne den Cache waere das ein
 * GET je Vorkommen.
 *
 * <p>Der {@code catchError} raeumt den Eintrag NICHT: eine fehlende Animationsdatei ist ein
 * Deployment-Fehler und aendert sich innerhalb einer Sitzung nicht. Zurueck kommt {@code null}, die
 * Komponente rendert dann nichts — wie bisher, nur ohne Konsolenrauschen.
 */
@Injectable({ providedIn: 'root' })
export class LottieAssetCache {
  private readonly http = inject(HttpClient)
  private readonly cache = new Map<string, Observable<unknown>>()

  load$(asset: string): Observable<unknown> {
    const vorhanden = this.cache.get(asset)
    if (vorhanden) {
      return vorhanden
    }
    const geladen = this.http.get(asset).pipe(
      catchError(() => of(null)),
      shareReplay({ bufferSize: 1, refCount: false }),
    )
    this.cache.set(asset, geladen)
    return geladen
  }
}

@Component({
  selector: 'lib-lottie-icon',
  imports: [LottieComponent],
  templateUrl: './lottie-icon.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './lottie-icon.component.scss',
})
export class LottieIconComponent {
  private readonly assets = inject(LottieAssetCache)

  readonly asset = input.required<string>()

  readonly size = input<string>('5rem')
  readonly height = input<string | undefined>()
  readonly width = input<string | undefined>()
  readonly loop = input<number | boolean>(true)

  /**
   * Abspielgeschwindigkeit, 1 = wie exportiert. Kleinere Werte laufen langsamer.
   *
   * Steht hier und nicht in der Animationsdatei: die Datei kommt aus After Effects und wird beim
   * naechsten Export neu geschrieben — eine dort geaenderte Bildrate waere jedes Mal wieder weg.
   * `setSpeed` ist zudem die einzige Stellschraube, die auch fuer eine fremde Animation gilt.
   */
  readonly speed = input(1)

  /**
   * Wer im Betriebssystem „Bewegung reduzieren" gesetzt hat, bekommt ein Standbild statt der
   * Wiedergabe — `autoplay: false`, und {@link uebernehmeEinstellungen} stellt das LETZTE Bild ein.
   *
   * Das letzte, nicht das erste: viele Animationen zeichnen sich aus dem Nichts auf, ihr Frame 0 ist
   * leer. Die Erfolgs-Animation der SEPA-Danke-Seite und der Brief im Unterschriften-Schritt waren
   * so bis 10/2026 fuer genau diese Nutzer unsichtbar — unter Windows schaltet „Animationseffekte
   * aus" (in Firmen- und Terminalserver-Umgebungen oft Vorgabe) die Einstellung in Edge und Chrome
   * ein. Das Endbild ist bei allen eingesetzten Animationen aussagekraeftig (geprueft 2026-10-15).
   *
   * Das ist kein Detail fuer eine Handvoll Leute: die Einstellung setzt, wer von Bewegung
   * Schwindel oder Kopfschmerzen bekommt, und eine dauerhaft laufende Schleife ist genau der Fall,
   * den sie meint. Per CSS ist das nicht loesbar — lottie zeichnet aus JavaScript.
   *
   * Einmal beim Anlegen gelesen und nicht als Signal: die Einstellung aendert sich waehrend einer
   * Sitzung praktisch nie, und ein Wechsel zur Laufzeit waere den Aufwand nicht wert. Der
   * `typeof`-Test haelt Umgebungen ohne `matchMedia` (Tests, SSR) heraus.
   */
  private readonly bewegungReduzieren = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches

  /**
   * Die Animationsdaten holen wir SELBST, statt lottie ueber {@code path} laden zu lassen.
   *
   * <p><b>Warum:</b> lottie-webs eigener Loader setzt {@code xhr.responseType = 'json'} und liest im
   * Anschluss trotzdem {@code responseText} — in Firefox wirft dieser Getter dann
   * {@code DOMException: responseText is only available if responseType is '' or 'text'}. Die
   * Animation lief zwar weiter (lottie faellt auf {@code xhr.response} zurueck), aber jede Seite mit
   * einem Patienten-Avatar warf die Meldung in die Konsole — und eine Konsole voller roter Meldungen
   * kostet beim naechsten echten Fehler die Zeit, ihn zu finden.
   *
   * <p>Ueber {@code animationData} laeuft kein XHR der Bibliothek mehr. Der Ladeweg ist damit unser
   * {@code HttpClient} — inklusive Interceptor-Kette und einem Cache je Datei
   * ({@link LottieAssetCache}), den lottie so nicht hatte.
   */
  private readonly animation = rxResource({
    params: () => this.asset(),
    stream: ({ params }) => this.assets.load$(params),
  })

  readonly animationsDaten = computed(() => (this.animation.hasValue() ? this.animation.value() : null))

  readonly options = computed<AnimationOptions>(() => ({
    animationData: this.animationsDaten(),
    loop: this.loop(),
    autoplay: !this.bewegungReduzieren,
  }))

  /**
   * `setSpeed` und `goToAndStop` gehen erst, wenn lottie die Animation angelegt hat — es gibt keine
   * Option dafuer in der Konfiguration. Die Werte werden beim Anlegen einmal uebernommen; aendert
   * sich `speed` spaeter, bliebe es beim alten Wert. Das ist bewusst so: eine Geschwindigkeit, die
   * sich zur Laufzeit aendert, hat hier keinen Anwendungsfall und braeuchte einen gehaltenen Verweis
   * auf das AnimationItem samt Aufraeumen.
   */
  protected uebernehmeEinstellungen(animation: AnimationItem) {
    animation.setSpeed(this.speed())
    if (this.bewegungReduzieren) {
      animation.goToAndStop(Math.max(0, animation.totalFrames - 1), true)
    }
  }
}
